/**
 * Per-network feature extension identifiers, as the v3 API returns them in
 * `network.extensions[].extension` (populated via `embed=extensions` — see
 * NetworkServiceV2.getNetwork). Presence in the array means the extension is on for
 * that network; there is no separate enabled flag.
 *
 * Read by NetworkExtensionGuard (`data.requiresExtension`) and by the surfaces that
 * hide themselves on a network without the extension.
 */
export const NETWORK_EXTENSIONS = {
    AI_ASSISTANT: 'AI_ASSISTANT',
    IPSEC_TUNNELER: 'IPSEC_TUNNELER',
    HTTP_SHARES: 'HTTP_SHARES',
    TCP_SHARES: 'TCP_SHARES',
} as const;

/**
 * The extensions an operator can switch on and off, in the order the Operations >
 * Management page lists them. `label` is what that page shows; the identifiers above are
 * never shown to a user.
 */
export const TOGGLEABLE_NETWORK_EXTENSIONS: { extension: string; label: string; description: string }[] = [
    {
        extension: NETWORK_EXTENSIONS.IPSEC_TUNNELER,
        label: 'IPsec Tunnelers',
        description: 'Adds the IPsec Tunnelers page, for site-to-site IPsec tunnels into this network. Used in Frontdoor and Network.',
    },
    {
        extension: NETWORK_EXTENSIONS.HTTP_SHARES,
        label: 'Frontdoor HTTP Shares',
        description: 'Adds the Frontdoor HTTP Shares page, for publishing services in this network over HTTP(S).',
    },
    {
        extension: NETWORK_EXTENSIONS.TCP_SHARES,
        label: 'Frontdoor TCP Shares',
        description: 'Adds the Frontdoor TCP Shares page, for publishing services in this network over raw TCP.',
    },
    {
        extension: NETWORK_EXTENSIONS.AI_ASSISTANT,
        label: 'AI Assistant',
        description: 'Adds the in-console AI chat assistant and its settings page for this network.',
    },
];

/** True when `network` has `extension` enabled. An unresolved extensions array counts as off. */
export function hasNetworkExtension(network: any, extension: string): boolean {
    return !!network?.extensions?.some((e: { extension: string }) => e.extension === extension);
}
